/**
 * SKOP Fee & User Management
 *
 * Flat 1% trade fee on every buy and sell. No subscriptions.
 * All features are free and unlimited.
 */

import { TRADE_FEE_BPS } from '@shared/types/users';

export { TRADE_FEE_BPS };

// ============================================================================
// User Profile
// ============================================================================

export interface UserProfile {
  walletAddress: string;
  createdAt: number;
  tradeCount: number;
  totalVolumeUsd: number;
  referralCode?: string;
  referredBy?: string;
}

const USER_PROFILE_KEY = 'skop_user_profile';
const USAGE_KEY = 'skop_usage';

/**
 * Get user profile from storage
 */
export function getUserProfile(walletAddress: string): UserProfile | null {
  try {
    const stored = localStorage.getItem(`${USER_PROFILE_KEY}_${walletAddress}`);
    if (!stored) return null;
    return JSON.parse(stored) as UserProfile;
  } catch {
    return null;
  }
}

/**
 * Save user profile to storage
 */
export function saveUserProfile(profile: UserProfile): void {
  localStorage.setItem(
    `${USER_PROFILE_KEY}_${profile.walletAddress}`,
    JSON.stringify(profile)
  );
}

/**
 * Create or get user profile
 */
export function getOrCreateProfile(walletAddress: string): UserProfile {
  const existing = getUserProfile(walletAddress);
  if (existing) return existing;

  const newProfile: UserProfile = {
    walletAddress,
    createdAt: Date.now(),
    tradeCount: 0,
    totalVolumeUsd: 0,
  };

  saveUserProfile(newProfile);
  return newProfile;
}

// ============================================================================
// Fee Calculation (flat 1% on every trade)
// ============================================================================

/**
 * Get trading fee in basis points — always 1% (100 bps)
 */
export function getTradingFeeBps(): number {
  return TRADE_FEE_BPS;
}

/**
 * Calculate fee amount in SOL
 */
export function calculateFee(amountSol: number): number {
  return amountSol * (TRADE_FEE_BPS / 10000);
}

/**
 * Get fee display string
 */
export function getFeeDisplayString(): string {
  return '1%';
}

// ============================================================================
// Usage Tracking (analytics only, not for gating)
// ============================================================================

interface DailyUsage {
  date: string; // YYYY-MM-DD
  lookups: number;
  simulations: number;
  trades: number;
  volumeUsd: number;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get usage for today
 */
export function getTodayUsage(walletAddress: string): DailyUsage {
  try {
    const today = getTodayString();
    const stored = localStorage.getItem(`${USAGE_KEY}_${walletAddress}_${today}`);

    if (stored) {
      return JSON.parse(stored);
    }

    return { date: today, lookups: 0, simulations: 0, trades: 0, volumeUsd: 0 };
  } catch {
    return { date: getTodayString(), lookups: 0, simulations: 0, trades: 0, volumeUsd: 0 };
  }
}

/**
 * Track a trade (analytics only)
 */
export function trackTrade(walletAddress: string, volumeUsd: number): void {
  // Update daily usage
  const usage = getTodayUsage(walletAddress);
  usage.trades += 1;
  usage.volumeUsd += volumeUsd;
  localStorage.setItem(
    `${USAGE_KEY}_${walletAddress}_${usage.date}`,
    JSON.stringify(usage)
  );

  // Update profile totals
  const profile = getOrCreateProfile(walletAddress);
  profile.tradeCount += 1;
  profile.totalVolumeUsd += volumeUsd;
  saveUserProfile(profile);
}

// ============================================================================
// Referral System
// ============================================================================

/**
 * Generate referral code for user
 */
export function generateReferralCode(walletAddress: string): string {
  const profile = getOrCreateProfile(walletAddress);

  if (!profile.referralCode) {
    profile.referralCode = `SKOP_${walletAddress.slice(0, 6).toUpperCase()}`;
    saveUserProfile(profile);
  }

  return profile.referralCode;
}

/**
 * Apply referral code
 */
export function applyReferralCode(walletAddress: string, referralCode: string): boolean {
  const existingCode = generateReferralCode(walletAddress);
  if (referralCode === existingCode) return false;

  const profile = getOrCreateProfile(walletAddress);
  if (profile.referredBy) return false;

  profile.referredBy = referralCode;
  saveUserProfile(profile);

  return true;
}
